import { Dispatch, SetStateAction, useEffect, useState } from "react"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { CustomInputGroups } from "types"
import { filterInputName } from "utils/filter"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import ToggleInput from "components/inputs/toggleInput"
import SelectInput from "components/inputs/selectInput"
import useLayout from "hooks/useLayout"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import { logger } from "utils/secureLogger"
import Form from "components/forms/form"

interface AddCustomInputFormProps {
    setGroups: Dispatch<SetStateAction<CustomInputGroups>>
    groupName: string
    groups: CustomInputGroups
}

const AddCustomInputForm: React.FC<AddCustomInputFormProps> = ({ setGroups, groupName, groups }) => {

    const { t } = useTranslation()
    const [errors, setErrors] = useState<string[]>([]);
    const [isValid, setIsValid] = useState<boolean>(true)

    const [name, setName] = useState<string>(``)
    const [type, setType] = useState<string>(`text`)
    const [required, setRequired] = useState<boolean>(false)
    const [defaultValue, setDefaultValue] = useState<string>(``)
    const [options, setOptions] = useState<string[]>([])
    const [optionsText, setOptionsText] = useState<string>(``)

    const {
        closeDialog
    } = useLayout()

    // Handle form submission.
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        // Check if the name is already in use in any group.
        if (Object.keys(groups).some(groupName => 
            Object.keys(groups[groupName].customInputs).map(inputName => inputName.toLowerCase()).includes(name.toLowerCase())
        )) {
            setErrors([t('nameAlreadyExists')])
            return
        }
        // Create new groups.
        const newInput: any = {
            type,
            required,
            defaultValue,
        }
        if (type === 'select' || type === 'radio') {
            newInput.options = options
        }

        const newGroups = {
            ...groups,
            [groupName]: {
            ...groups[groupName],
            customInputs: {
                ...groups[groupName].customInputs,
                [name]: newInput
            },
            customInputOrder: [...groups[groupName].customInputOrder, name]
            }
        }
        logger.debug('Custom input groups updated', { groupName, inputName: name })
        // Update the groups.
        setGroups(newGroups)
        // Close the dialog.
        closeDialog()
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(filterInputName(e.target.value, false, 30, true))
    }

    // Handle type change.
    const handleTypeChange = (targetType: string) => {
        setType(targetType)
        if (targetType === `checkbox`) setDefaultValue(`false`)
        if (targetType === `select` || targetType === `radio`) {
            setDefaultValue(``)
            setOptions([])
            setOptionsText(``)
        }
    }

    // Handle default if checked change.
    const handleDefaultIsCheckedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultValue(e.target.checked ? `true` : `false`)
    }

    // Handle default value change.
    const handleDefaultValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultValue(e.target.value)
    }

    // Handle required change.
    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRequired(e.target.checked)
    }

    // Handle options text change.
    const handleOptionsTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value
        setOptionsText(text)
        // Split by newlines and filter out empty strings
        const optionsList = text.split('\n').filter(option => option.trim() !== '')
        setOptions(optionsList)
    }

    useEffect(() => {
        const unsubscribe = () => {
            setIsValid(true)
            if (!name || name.length < 1) {
                setIsValid(false)
            }
            // Require options for select and radio types
            if ((type === 'select' || type === 'radio') && options.length === 0) {
                setIsValid(false)
            }
        }
        unsubscribe()
        return () => {
            unsubscribe()
        }
    }, [name, type, options])

    // Return the JSX.
    return (
        <Form onSubmit={handleSubmit}>
            <Section>
            <div className={`group`}>

            <TextInput label={t('name')} value={name} onChange={handleNameChange} required />

            <SelectInput
                label={t('type')}
                value={type}
                options={[
                    { value: `text`, label: t('text') },
                    { value: `number`, label: t('number') },
                    { value: `checkbox`, label: t('checkbox') },
                    { value: `textarea`, label: t('textarea') },
                    { value: `select`, label: t('select') },
                    { value: `radio`, label: t('radio') }
                ]}
                onChange={(e) => handleTypeChange(e.target.value)}
                required />

            <ToggleInput label={t('required')} checked={required} onChange={handleRequiredChange} />

            {type !== `checkbox` && type !== `select` && type !== `radio` && (
                <TextInput 
                    label={t('defaultValue')} 
                    value={defaultValue} 
                    onChange={handleDefaultValueChange}
                    maxLength={type === 'textarea' ? 2000 : 200}
                    showCharacterCount={true}
                />
            )}

            {type === `checkbox` && (
                <ToggleInput label={t('defaultIsChecked')} checked={defaultValue === 'true'} onChange={handleDefaultIsCheckedChange} />
            )}

            {(type === `select` || type === `radio`) && (
                <TextAreaInput 
                    label={t('options')} 
                    value={optionsText} 
                    onChange={handleOptionsTextChange} 
                    placeholder={t('enterOptionsOnePerLine')}
                    rows={4}
                    required
                />
            )}

            <ButtonGroup>
                <Button wideOnMobile type="button" design="white" onClick={closeDialog}><CloseIcon /><span>{t('buttonCancel')}</span></Button>
                <Button wideOnMobile type="submit" design="primary" disabled={!isValid}><AddIcon /><span>{t('buttonAdd')}</span></Button>
            </ButtonGroup>

            {errors.length > 0 && <FormErrors errors={errors} />}
        </div>
            </Section>
        </Form>
    )

}

export default AddCustomInputForm



